import ApiProvider from 'libs/providers/api/api.provider';
import { Observable } from 'rxjs';
import { ProfilingPostData } from '../interfaces/profiling-post-data';
import QuotationAffiliate from '../models/quotation-affiliate';
import { Quote } from '../models/quotation';
import { AjaxResponse } from 'rxjs/ajax';
import RequestProvider from 'domains/core-base/request/providers/request-provider';
import env from 'libs/constants/env';
import QuotationDto, {
  CashVehicleQuoteApiResponse,
  CashVehicleQuoteWrapperRequest,
  RecommendedPlansApiResponse,
  RecommendedPlansWrapperRequest,
  RoutingPlansApiResponse,
  RoutingPlansWrapperRequest,
} from '../dtos/quotation-dto';
import QuotationMapping from '../mappings/quotation-mapping';
import { ZoneDto } from '../dtos/zone-dto';
import { BanksDto } from '../dtos/banks-dto';
import { RemarketingDto } from '../dtos/remarketing-dto';
import { QuotationEssential } from '../models/quotation-essential';
import QuotationEssentialResponseDto from '../dtos/quotation-essential-dto';

const BASE_URL = String(import.meta.env.VITE_APP_SERVER_URL);

class QuotationProvider {
  constructor(public provider: ApiProvider = new ApiProvider()) {}

  static readonly _provider: ApiProvider = new ApiProvider();

  static readonly requestProvider = RequestProvider.getInstance();

  static readonly quote = (params: {
    authorization: string;
    quote: Quote;
    recaptcha: string;
  }): Observable<AjaxResponse<QuotationDto>> => {
    const request = RequestProvider.getInstance();
    const data = new QuotationMapping().formatCreateQuote(params.quote);
    return this._provider.post(
      `${env.CONTEXT_PATH}/cotizaciones`,
      {
        ...data,
      },
      {
        Authorization: params.authorization,
        'Transaccion-Id': request.getRequestId(),
        'g-recaptcha-response': params.recaptcha,
      },
    );
  };

  static readonly cashVehicleQuote = ({
    payload,
    token,
    recaptchaToken,
  }: CashVehicleQuoteWrapperRequest): Observable<
    AjaxResponse<CashVehicleQuoteApiResponse>
  > =>
    this._provider.post(
      `${env.CONTEXT_PATH}/cotizaciones/auto-efectivo`,
      {
        ...new QuotationMapping().toCashVehicleQuoteApiRequest(payload),
      },
      {
        Authorization: token,
        'Transaccion-Id': this.requestProvider.getRequestId(),
        'g-recaptcha-response': recaptchaToken,
      },
    );

  affiliate(
    data: QuotationAffiliate,
    options = { token: '' },
  ): Observable<any> {
    return this.provider.post(
      `${BASE_URL}/ux-emisiones-autos-gen/ecommerce-autos/emision/v1/v1/issue/affiliate`,
      {
        ...data,
      },
      {
        'ocp-Apim-Subscription-Key': import.meta.env.VITE_APP_SUBSCRIPTION_KEY,
        Authorization: `Bearer ${options.token}`,
      },
    );
  }

  static readonly getPlans = (params: {
    authorization: string;
    recaptcha: string;
    channel?: string;
  }): Observable<AjaxResponse<any>> => {
    const request = RequestProvider.getInstance();
    const queryParam = params.channel ? `?channel=${params.channel}` : '';
    return this._provider.get(
      `${env.CONTEXT_PATH}/cotizaciones/obtener-planes${queryParam}`,
      {
        Authorization: params.authorization,
        'Transaccion-Id': request.getRequestId(),
        'g-recaptcha-response': params.recaptcha,
      },
    );
  };

  static readonly getBanks = (params: {
    authorization: string;
  }): Observable<AjaxResponse<BanksDto[]>> => {
    const request = RequestProvider.getInstance();
    return this._provider.get(`${env.CONTEXT_PATH}/bancos`, {
      Authorization: params.authorization,
      'Transaccion-Id': request.getRequestId(),
    });
  };

  static readonly getZones = (params: {
    plate: string;
    authorization: string;
    recaptcha: string;
  }): Observable<AjaxResponse<ZoneDto[]>> => {
    const request = RequestProvider.getInstance();
    return this._provider.get(
      `${env.CONTEXT_PATH}/vehiculos/${params.plate}/obtener-area-circulacion`,
      {
        Authorization: params.authorization,
        'Transaccion-Id': request.getRequestId(),
        'g-recaptcha-response': params.recaptcha,
      },
    );
  };

  profiling(data: ProfilingPostData, options = { token: '' }): Observable<any> {
    return this.provider.post(
      `${BASE_URL}/ux-ofertas-autos-gen/ecommerce-autos/cotizacion/v1/offers/recommended`,
      {
        ...data,
      },
      {
        'ocp-Apim-Subscription-Key': import.meta.env.VITE_APP_SUBSCRIPTION_KEY,
        Authorization: `Bearer ${options.token}`,
      },
    );
  }

  allPlans(options = { token: '' }): Observable<any> {
    return this.provider.get(
      `${BASE_URL}/ux-ofertas-autos-gen/ecommerce-autos/cotizacion/v1/offers`,
      {
        'ocp-Apim-Subscription-Key': import.meta.env.VITE_APP_SUBSCRIPTION_KEY,
        Authorization: `Bearer ${options.token}`,
      },
    );
  }

  static readonly getRecommendedPlans = ({
    payload,
    token,
    recaptchaToken,
  }: RecommendedPlansWrapperRequest): Observable<
    AjaxResponse<RecommendedPlansApiResponse>
  > =>
    this._provider.post(
      `${env.CONTEXT_PATH}/planes/recomendados`,
      {
        ...new QuotationMapping().formatGetRecommendedPlansRequest(payload),
      },
      {
        Authorization: token,
        'Transaccion-Id': this.requestProvider.getRequestId(),
        'g-recaptcha-response': recaptchaToken,
      },
    );

  static readonly getRoutingPlans = ({
    payload,
    token,
    recaptchaToken,
  }: RoutingPlansWrapperRequest): Observable<
    AjaxResponse<RoutingPlansApiResponse>
  > =>
    this._provider.post(
      `${env.CONTEXT_PATH}/planes/recomendados/enrutamiento`,
      {
        ...new QuotationMapping().formatGetRoutingPlansRequest(payload),
      },
      {
        Authorization: token,
        'Transaccion-Id': this.requestProvider.getRequestId(),
        'g-recaptcha-response': recaptchaToken,
      },
    );

  static readonly getRemarketingQuote = (params: {
    requestId: string;
    authorization: string;
    recaptcha: string;
  }): Observable<AjaxResponse<RemarketingDto>> => {
    const request = RequestProvider.getInstance();

    return this._provider.get(
      `${env.CONTEXT_PATH}/recomercializacion/autoefectivo/${params.requestId}`,
      {
        Authorization: params.authorization,
        'Transaccion-Id': request.getRequestId(),
        'g-recaptcha-response': params.recaptcha,
      },
    );
  };

  static readonly getEssentialPlan = (params: {
    authorization: string;
    quote: QuotationEssential;
    recaptcha: string;
  }): Observable<AjaxResponse<QuotationEssentialResponseDto>> => {
    const request = RequestProvider.getInstance();
    const data = new QuotationMapping().formatEssentialQuote(params.quote);
    return this._provider.post(
      `${env.CONTEXT_PATH}/cotizaciones/bex-esencial`,
      {
        ...data,
      },
      {
        Authorization: params.authorization,
        'Transaccion-Id': request.getRequestId(),
        'g-recaptcha-response': params.recaptcha,
      },
    );
  };
}
export default QuotationProvider;
